import React, { useState, useEffect } from 'react';
import { Button, Input } from '@bnvsrnd/brand-assets';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toastBus';
import api from '../../utils/api';
import { setGuestSession, getGuestSession, clearGuestSession } from '../../utils/guestAuth';
import ConsentModal from '../Auth/ConsentModal';

/**
 * 무계정 게스트 학생 로그인 폼 (수업 코드 + 학번 + 이름).
 * 표준 학생 로그인 페이지(/student-login)와 로그인 화면의 학생 모드에서 공용으로 사용한다.
 * 입장 성공 시 학생 홈(/student/home)으로 이동한다.
 */
function StudentLoginForm() {
    const navigate = useNavigate();
    const [classCode, setClassCode] = useState('');
    const [studentNo, setStudentNo] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    // 아직 최신 이용약관에 동의한 적 없는 학생이 입장을 시도하면 서버가 428을 준다 —
    // 입장 정보(수업코드/학번/이름)는 이미 맞았으니, 동의를 받아 같은 정보로 재입장만 시도하면 된다.
    const [consentPending, setConsentPending] = useState(null); // { classCode, studentNo, name } | null
    const [consentSubmitting, setConsentSubmitting] = useState(false);
    // 재접속 — 이 브라우저에 남아 있는 입장 기록(게스트 토큰)이 아직 유효하면 그 기록으로 바로 이어 간다.
    // { name, studentNo, classTitle, game: { href, label, spa } | null }
    const [resume, setResume] = useState(null);

    useEffect(() => {
        const saved = getGuestSession();
        if (!saved?.token) return undefined;
        let alive = true;
        (async () => {
            // 계정 로그인(auth)이 같이 있으면 api 인터셉터가 그 토큰을 붙이므로, 게스트 토큰을 직접 붙여 확인한다.
            const headers = { Authorization: `Bearer ${saved.token}` };
            const base = api.defaults.baseURL;
            try {
                const meRes = await fetch(`${base}/student/me`, { headers });
                if (!meRes.ok) {
                    // 만료(24시간)·코드 재발급·명단 제외 — 기록을 지우고 새로 입장하게 한다.
                    if (meRes.status === 401 || meRes.status === 404) clearGuestSession();
                    return;
                }
                const me = (await meRes.json())?.enrollment || {};
                let game = null;
                try {
                    const simRes = await fetch(`${base}/student-simulations/me`, { headers });
                    const sim = (await simRes.json())?.data;
                    if (sim?.session?.simulationKey === 'diagnosis-league' && sim.team && sim.session.status === 'running') {
                        game = { href: '/games/diabetes-league/index.html', label: '진행 중인 AI 진단 랩 리그로 돌아가기' };
                    } else if (sim?.session) {
                        game = { href: '/student/simulation', label: '진행 중인 팀 시뮬레이션으로 돌아가기', spa: true };
                    } else if (sim?.finishedLeague) {
                        game = { href: '/games/diabetes-league/index.html', label: '리그 최종 결과 보기' };
                    }
                } catch {
                    /* 시뮬레이션 정보가 없어도 이어서 하기는 된다 */
                }
                if (alive) {
                    setResume({ name: me.name || saved.enrollment?.name || '', studentNo: me.studentNo || saved.enrollment?.studentNo || '', classTitle: saved.classInfo?.title || '', game });
                }
            } catch {
                /* 네트워크 오류 — 새로 입장하는 폼만 보여 준다 */
            }
        })();
        return () => { alive = false; };
    }, []);

    const goResume = (target) => {
        // 게임 화면은 정적 페이지라 전체 이동, 나머지는 앱 안에서 이동한다.
        if (target && !target.spa) {
            window.location.href = target.href;
            return;
        }
        navigate(target?.href || '/student/home', { replace: true });
    };

    // 수업 코드: 자동 대문자화 + 영대문자/숫자만, 6자리 제한
    const handleClassCodeChange = (e) => {
        const value = (e.target.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        setClassCode(value.slice(0, 6));
    };

    const enterWithPayload = async (payload) => {
        const response = await api.post('/student/enter', payload);
        const { token, enrollment, classInfo, warning } = response.data || {};
        setGuestSession({ token, enrollment, classInfo });

        if (warning) {
            toast.info(warning);
        }

        navigate('/student/home', { replace: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const code = classCode.trim();
        const no = studentNo.trim();
        const studentName = name.trim();

        if (!code || !no || !studentName) {
            toast.warn('수업 코드, 학번, 이름을 모두 입력하세요.');
            return;
        }
        if (code.length !== 6) {
            toast.warn('수업 코드는 6자리여야 합니다.');
            return;
        }

        try {
            setLoading(true);
            await enterWithPayload({ classCode: code, studentNo: no, name: studentName });
        } catch (err) {
            const res = err.response;
            if (res?.status === 428 && res.data?.code === 'CONSENT_REQUIRED') {
                setConsentPending({ classCode: code, studentNo: no, name: studentName });
                return;
            }
            toast.error(res?.data?.message || '입장할 수 없는 수업코드입니다.');
        } finally {
            setLoading(false);
        }
    };

    // checked = { terms, privacy, promotional } — ConsentModal이 넘겨준다.
    // 홍보 활용(promotional)은 선택 항목이라 체크 여부와 무관하게 항상 입장은 진행된다.
    const handleConsentAgree = async (checked) => {
        if (!consentPending) return;
        setConsentSubmitting(true);
        try {
            await enterWithPayload({
                ...consentPending,
                agreedToTerms: true,
                agreedToPrivacy: true,
                agreedToPromotionalUse: !!checked?.promotional
            });
            setConsentPending(null);
        } catch (err) {
            toast.error(err.response?.data?.message || '입장할 수 없는 수업코드입니다.');
            setConsentPending(null);
        } finally {
            setConsentSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {resume && (
                <div style={{ marginBottom: 'var(--space-5)', padding: 'var(--space-4)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--background-subtle)' }}>
                    <div style={{ font: 'var(--label-default-semibold)', marginBottom: 'var(--space-1)' }}>이 브라우저에 입장한 기록이 있어요</div>
                    <div style={{ font: 'var(--body-small)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
                        {[resume.classTitle, [resume.studentNo, resume.name].filter(Boolean).join(' ')].filter(Boolean).join(' · ')} — 본인이면 그대로 이어서 하세요.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {resume.game && (
                            <Button variant="primary" type="button" style={{ width: '100%' }} onClick={() => goResume(resume.game)}>
                                {resume.game.label}
                            </Button>
                        )}
                        <Button variant={resume.game ? 'secondary' : 'primary'} type="button" style={{ width: '100%' }} onClick={() => goResume(null)}>
                            이어서 하기 (내 수업)
                        </Button>
                        <Button variant="ghost" type="button" style={{ width: '100%' }} onClick={() => { clearGuestSession(); setResume(null); }}>
                            다른 학생으로 입장하기
                        </Button>
                    </div>
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <Input
                    type="text"
                    label="수업 코드"
                    placeholder="6자리 코드 (예: ABC123)"
                    value={classCode}
                    onChange={handleClassCodeChange}
                    maxLength={6}
                    autoComplete="off"
                    autoCapitalize="characters"
                    disabled={loading}
                />
                <Input
                    type="text"
                    label="학번"
                    placeholder="본인 학번"
                    value={studentNo}
                    onChange={(e) => setStudentNo(e.target.value)}
                    autoComplete="off"
                    disabled={loading}
                />
                <Input
                    type="text"
                    label="이름"
                    placeholder="본인 이름"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="off"
                    disabled={loading}
                />

                <Button
                    variant="primary"
                    type="submit"
                    loading={loading}
                    style={{ width: '100%' }}
                >
                    입장하기
                </Button>
            </div>

            <ConsentModal
                open={!!consentPending}
                group="student"
                submitting={consentSubmitting}
                onAgree={handleConsentAgree}
                onClose={() => setConsentPending(null)}
            />
        </form>
    );
}

export default StudentLoginForm;
